import { useState } from "react";
import { Search, Grid3x3, List, Clock, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { PxMark } from "@/components/PxMark";

interface GridProject {
  id: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

interface ProjectsGridProps {
  projects: GridProject[];
  activeProjectId: string | null;
  onSelectProject: (id: string) => void;
  onNewProject: () => void;
  onDeleteProject: (id: string) => void;
  isLoading?: boolean;
}

type ThumbnailType = 'landing' | 'dashboard' | 'form' | 'portfolio' | 'blog' | 'ecommerce';

const getThumbnailType = (name: string, index: number): ThumbnailType => {
  const lower = name.toLowerCase();
  if (lower.includes('landing') || lower.includes('page')) return 'landing';
  if (lower.includes('dashboard') || lower.includes('analytics')) return 'dashboard';
  if (lower.includes('form') || lower.includes('connexion') || lower.includes('login')) return 'form';
  if (lower.includes('portfolio')) return 'portfolio';
  if (lower.includes('blog')) return 'blog';
  if (lower.includes('shop') || lower.includes('commerce') || lower.includes('boutique')) return 'ecommerce';

  // Default based on index
  const types: ThumbnailType[] = ['landing', 'dashboard', 'form', 'portfolio', 'blog', 'ecommerce'];
  return types[index % types.length];
};

// Vignettes factices monochromes : blocs encre 10 % / 25 % posés sur surface-3
const renderThumbnail = (type: ThumbnailType) => {
  switch (type) {
    case 'landing':
      return (
        <div className="h-full p-6 bg-surface-3">
          <div className="space-y-2">
            <div className="h-2 w-16 bg-foreground/10" />
            <div className="h-3 w-full bg-foreground/25" />
            <div className="h-3 w-4/5 bg-foreground/25" />
            <div className="h-2 w-3/5 bg-foreground/10 mt-2" />
            <div className="flex gap-2 mt-3">
              <div className="h-5 w-16 bg-foreground/25" />
              <div className="h-5 w-14 bg-foreground/10" />
            </div>
          </div>
        </div>
      );
    case 'dashboard':
      return (
        <div className="h-full p-6 bg-surface-3">
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 p-2 bg-foreground/10">
                <div className="h-1.5 w-10 bg-foreground/10 mb-1" />
                <div className="h-2 w-6 bg-foreground/25" />
              </div>
            ))}
          </div>
          <div className="h-16 p-3 bg-foreground/10">
            <div className="flex items-end gap-0.5 h-full">
              {[40, 70, 50, 90, 60, 85, 45].map((height, i) => (
                <div key={i} className="flex-1 bg-foreground/25" style={{ height: `${height}%` }} />
              ))}
            </div>
          </div>
        </div>
      );
    case 'form':
      return (
        <div className="h-full p-6 flex items-center justify-center bg-surface-3">
          <div className="w-full max-w-[160px] space-y-3">
            <div className="h-2 w-16 bg-foreground/25 mb-4" />
            <div className="h-7 bg-foreground/10" />
            <div className="h-7 bg-foreground/10" />
            <div className="h-7 bg-foreground/25 mt-4" />
          </div>
        </div>
      );
    case 'portfolio':
      return (
        <div className="h-full p-6 bg-surface-3">
          <div className="text-center mb-4">
            <div className="w-10 h-10 bg-foreground/25 mx-auto mb-2" />
            <div className="h-2 w-16 bg-foreground/25 mx-auto mb-1" />
            <div className="h-1.5 w-12 bg-foreground/10 mx-auto" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-square bg-foreground/10" />
            ))}
          </div>
        </div>
      );
    case 'blog':
      return (
        <div className="h-full p-6 bg-surface-3">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="h-2 w-3/4 bg-foreground/25" />
                <div className="h-1.5 w-full bg-foreground/10" />
                <div className="h-1.5 w-5/6 bg-foreground/10" />
              </div>
            ))}
          </div>
        </div>
      );
    case 'ecommerce':
      return (
        <div className="h-full p-6 bg-surface-3">
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="aspect-square bg-foreground/10" />
                <div className="h-1.5 w-full bg-foreground/10" />
                <div className="h-2 w-10 bg-foreground/25" />
              </div>
            ))}
          </div>
        </div>
      );
  }
};

const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return "il y a quelques minutes";
  if (diffHours < 24) return `il y a ${diffHours}h`;
  if (diffDays === 1) return "il y a 1j";
  return `il y a ${diffDays}j`;
};

export const ProjectsGrid = ({
  projects,
  onSelectProject,
  onNewProject,
  isLoading = false
}: ProjectsGridProps) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort by most recent
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt);
    const dateB = new Date(b.updatedAt || b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="flex-1 flex flex-col bg-background overflow-hidden">
      {/* Header : label mono entre hairlines */}
      <div className="h-12 border-b border-border flex items-center justify-between px-6 shrink-0">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
            Projets
          </span>
          <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
            {filteredProjects.length}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-faint" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher…"
              className="pl-9 pr-3 h-8 w-[220px] bg-secondary border border-border text-[13px] text-foreground placeholder:text-faint outline-none focus:border-foreground transition-colors"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center border border-border">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "p-1.5 transition-colors",
                viewMode === 'grid' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
              )}
              aria-label="Vue grille"
            >
              <Grid3x3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "p-1.5 transition-colors",
                viewMode === 'list' ? "bg-secondary text-foreground" : "text-faint hover:text-foreground"
              )}
              aria-label="Vue liste"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <PxMark state="thinking" size={24} label="Chargement" />
              <span className="font-mono text-[11px] text-faint">Chargement…</span>
            </div>
          </div>
        ) : sortedProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <PxMark state="idle" size={24} />
            <p className="text-sm text-muted-foreground">Aucun projet pour l'instant.</p>
            <button
              onClick={onNewProject}
              className="h-9 px-4 bg-card border border-border text-[13px] font-medium text-foreground hover:bg-secondary transition-colors"
            >
              Décrire une app
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1800px] mx-auto">
            {sortedProjects.map((project, index) => (
              <div
                key={project.id}
                onClick={() => onSelectProject(project.id)}
                className="group cursor-pointer"
              >
                <div className="bg-card border border-border overflow-hidden transition-colors group-hover:bg-secondary">
                  {/* Thumbnail */}
                  <div className="relative aspect-[16/10] bg-surface-3 border-b border-border overflow-hidden">
                    {renderThumbnail(getThumbnailType(project.name, index))}
                  </div>

                  {/* Info */}
                  <div className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-base font-medium truncate text-foreground">
                        {project.name}
                      </h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 border border-border text-faint shrink-0">
                        v1
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 pt-1">
                      <Clock className="w-3 h-3 text-faint" />
                      <span className="font-mono text-[11px] text-faint">
                        {getRelativeTime(project.updatedAt || project.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-2">
            {sortedProjects.map((project, index) => (
              <div
                key={project.id}
                onClick={() => onSelectProject(project.id)}
                className="group cursor-pointer"
              >
                <div className="bg-card border border-border p-3 flex items-center gap-4 transition-colors group-hover:bg-secondary">
                  {/* Mini Thumbnail */}
                  <div className="w-24 h-16 overflow-hidden shrink-0 bg-surface-3 border border-border">
                    {renderThumbnail(getThumbnailType(project.name, index))}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium text-foreground truncate">
                        {project.name}
                      </h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 border border-border text-faint">
                        v1
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-faint" />
                      <span className="font-mono text-[11px] text-faint">
                        {getRelativeTime(project.updatedAt || project.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity px-2">
                    <ArrowRight className="w-4 h-4 text-foreground" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
